import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Eye, FileText, FolderClosed, FolderOpen, History, Loader2, Pencil, Search, Trash2, Upload, UploadCloud, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { fmtDate, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const sizeText = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const PREVIEWABLE = ["application/pdf", "image/jpeg", "image/png"];

function expiryState(d) {
  if (!d) return null;
  const days = Math.ceil((new Date(d + "T00:00:00") - new Date(todayISO() + "T00:00:00")) / 86400000);
  if (days < 0) return { tone: "bg-rose-500/10 text-rose-600 dark:text-rose-400", text: `Expired ${-days}d ago` };
  if (days <= 30) return { tone: "bg-amber-500/10 text-amber-700 dark:text-amber-400", text: `Expires in ${days}d` };
  return { tone: "bg-muted text-muted-foreground", text: `Valid to ${fmtDate(d)}` };
}

async function fetchBlob(projectId, id, inline) {
  const { data } = await api.get(`/projects/${projectId}/documents/${id}/file`, { params: inline ? { inline: true } : {}, responseType: "blob" });
  return data;
}

export default function ProjectDocuments() {
  const { projectId } = useProjectCtx();
  const [state, setState] = useState(null);
  const [folder, setFolder] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [upload, setUpload] = useState(null);       // { files, replaces } | null
  const [edit, setEdit] = useState(null);
  const [versions, setVersions] = useState(null);   // { doc, rows }
  const [del, setDel] = useState(null);
  const [drag, setDrag] = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/documents`, { params: { ...(folder ? { folder } : {}), ...(q ? { q } : {}) } });
      setState(data);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setLoading(false); }
  }, [projectId, folder, q]);
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);

  const canWrite = state?.can_write;
  const folders = state?.folders || [];

  const save = (blob, name) => { const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url); };
  const download = async (d) => { try { save(await fetchBlob(projectId, d.id, false), d.filename); } catch (err) { toast.error(getErrorMessage(err)); } };
  const preview = async (d) => { try { window.open(URL.createObjectURL(await fetchBlob(projectId, d.id, true)), "_blank", "noopener"); } catch (err) { toast.error(getErrorMessage(err)); } };
  const openVersions = async (d) => { try { const { data } = await api.get(`/projects/${projectId}/documents/${d.id}/versions`); setVersions({ doc: d, rows: data }); } catch (err) { toast.error(getErrorMessage(err)); } };
  const remove = async (all) => {
    try { await api.delete(`/projects/${projectId}/documents/${del.id}`, { params: all ? { all_versions: true } : {} }); toast.success("Document deleted"); setDel(null); setVersions(null); load(); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (canWrite && e.dataTransfer.files?.length) setUpload({ files: Array.from(e.dataTransfer.files), replaces: null }); };
  const stats = useMemo(() => [
    ["Documents", state?.total ?? 0, ""],
    ["Expiring in 30 days", state?.expiring_soon ?? 0, state?.expiring_soon ? "text-amber-600 dark:text-amber-400" : ""],
    ["Expired", state?.expired ?? 0, state?.expired ? "text-rose-600 dark:text-rose-400" : ""],
  ], [state]);

  return (
    <div className="space-y-5" data-testid="project-documents-page" onDragOver={(e) => { e.preventDefault(); if (canWrite) setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}>
      <PageHeader
        title="Documents"
        description="Contracts, drawings, permits, correspondence and site records for this project — one place, with versions and expiry dates."
        action={canWrite && (
          <>
            <input ref={fileInput} type="file" multiple hidden onChange={(e) => { if (e.target.files?.length) setUpload({ files: Array.from(e.target.files), replaces: null }); e.target.value = ""; }} />
            <Button onClick={() => fileInput.current?.click()} data-testid="upload-document-button"><Upload className="h-4 w-4" /> Upload</Button>
          </>
        )}
      />

      <div className="grid grid-cols-3 gap-3">
        {stats.map(([k, v, tone]) => (
          <div key={k} className="rounded-lg border border-border bg-card px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{k}</p>
            <p className={`mt-1 text-2xl font-semibold tabular-nums ${tone}`}>{v}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav className="rounded-lg border border-border bg-card p-2 text-sm" aria-label="Folders" data-testid="document-folders">
          {[{ name: "", label: "All documents", count: state?.total ?? 0 }, ...folders.map((f) => ({ name: f.name, label: f.name, count: f.count }))].map((f) => (
            <button key={f.name || "all"} type="button" onClick={() => setFolder(f.name)}
              className={`flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left transition-colors ${folder === f.name ? "bg-primary/10 font-medium text-primary" : "text-foreground hover:bg-muted"}`}>
              {folder === f.name ? <FolderOpen className="h-4 w-4 shrink-0" /> : <FolderClosed className="h-4 w-4 shrink-0 text-muted-foreground" />}
              <span className="min-w-0 flex-1 truncate">{f.label}</span>
              <span className="text-xs tabular-nums text-muted-foreground">{f.count}</span>
            </button>
          ))}
        </nav>

        <div className="min-w-0 space-y-3">
          <div className="relative max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, number, file name…" className="pl-8" data-testid="document-search" />
          </div>

          <div className={`rounded-lg border bg-card ${drag ? "border-primary ring-2 ring-primary/30" : "border-border"}`}>
            {loading && !state ? (
              <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : !state?.items.length ? (
              <div className="p-6">
                <EmptyState title={q || folder ? "No documents match" : "No documents yet"}
                  description={canWrite ? "Drag files here or click Upload." : "Nothing has been uploaded to this project."} testId="documents-empty" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Document</TableHead><TableHead>Expiry</TableHead>
                    <TableHead>Uploaded</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {state.items.map((d) => {
                      const ex = expiryState(d.expiry_date);
                      return (
                        <TableRow key={d.id} data-testid={`document-row-${d.id}`}>
                          <TableCell className="max-w-[26rem]">
                            <div className="flex items-start gap-2">
                              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className="truncate font-medium text-foreground">{d.title}</span>
                                  {d.version_count > 1 && <button type="button" onClick={() => openVersions(d)} className="rounded bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground hover:text-foreground">v{d.version}</button>}
                                </div>
                                <div className="truncate text-xs text-muted-foreground">{d.folder}{d.doc_number ? ` · ${d.doc_number}` : ""}{d.doc_date ? ` · ${fmtDate(d.doc_date)}` : ""}</div>
                                <div className="truncate text-[11px] text-muted-foreground/80">{d.filename} · {sizeText(d.size_bytes)}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{ex ? <span className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ${ex.tone}`}>{ex.text}</span> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                          <TableCell className="whitespace-nowrap text-xs"><div>{d.uploaded_by_name || "—"}</div><div className="text-muted-foreground">{fmtDate(d.created_at)}</div></TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-0.5">
                              {PREVIEWABLE.includes(d.content_type) && <Button size="icon" variant="ghost" title="Preview" onClick={() => preview(d)}><Eye className="h-4 w-4" /></Button>}
                              <Button size="icon" variant="ghost" title="Download" onClick={() => download(d)} data-testid={`download-${d.id}`}><Download className="h-4 w-4" /></Button>
                              {d.version_count > 1 && <Button size="icon" variant="ghost" title="Version history" onClick={() => openVersions(d)}><History className="h-4 w-4" /></Button>}
                              {canWrite && <>
                                <Button size="icon" variant="ghost" title="Upload a new version" onClick={() => setUpload({ files: [], replaces: d })}><RefreshCw className="h-4 w-4" /></Button>
                                <Button size="icon" variant="ghost" title="Edit details" onClick={() => setEdit({ ...d })}><Pencil className="h-4 w-4" /></Button>
                                <Button size="icon" variant="ghost" title="Delete" onClick={() => setDel(d)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                              </>}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
          {canWrite && <p className="text-xs text-muted-foreground">Drag and drop files anywhere on this page. Up to {state?.max_mb} MB each. Allowed: {(state?.allowed || []).join(" ")}</p>}
        </div>
      </div>

      {upload && <UploadDialog projectId={projectId} init={upload} folders={folders.map((f) => f.name)} defaultFolder={folder} state={state} onClose={() => setUpload(null)} onDone={() => { setUpload(null); load(); }} />}
      {edit && <EditDialog projectId={projectId} doc={edit} folders={folders.map((f) => f.name)} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}

      <Dialog open={!!versions} onOpenChange={(o) => !o && setVersions(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{versions?.doc.title}</DialogTitle><DialogDescription>Every uploaded version, newest first.</DialogDescription></DialogHeader>
          <div className="divide-y divide-border rounded border border-border text-sm">
            {versions?.rows.map((v) => (
              <div key={v.id} className="flex items-center gap-3 px-3 py-2">
                <span className="w-8 shrink-0 font-mono text-xs">v{v.version}</span>
                <div className="min-w-0 flex-1"><div className="truncate">{v.filename}</div><div className="text-xs text-muted-foreground">{fmtDate(v.created_at)} · {v.uploaded_by_name || "—"} · {sizeText(v.size_bytes)}</div></div>
                {v.is_latest && <span className="rounded bg-emerald-500/10 px-1.5 text-[10px] font-semibold text-emerald-600">Latest</span>}
                <Button size="icon" variant="ghost" onClick={() => download(v)} aria-label="Download"><Download className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete “{del?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>{del?.version_count > 1 ? "This document has older versions. Delete only this version, or all of them. " : ""}The file is removed from the server and cannot be recovered.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(false)} data-testid="confirm-delete-document">{del?.version_count > 1 ? "Delete this version" : "Delete"}</AlertDialogAction>
            {del?.version_count > 1 && <AlertDialogAction onClick={() => remove(true)}>Delete all versions</AlertDialogAction>}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FolderField({ value, onChange, folders }) {
  return (
    <>
      <Input list="doc-folders" value={value} onChange={(e) => onChange(e.target.value)} placeholder="Pick or type a new folder" data-testid="document-folder-input" />
      <datalist id="doc-folders">{folders.map((f) => <option key={f} value={f} />)}</datalist>
    </>
  );
}

export function UploadDialog({ projectId, init, folders, defaultFolder, state, onClose, onDone }) {
  const replaces = init.replaces;
  const [files, setFiles] = useState(init.files);
  const [folder, setFolder] = useState(replaces?.folder || defaultFolder || "Other");
  const [title, setTitle] = useState(replaces?.title || "");
  const [docNumber, setDocNumber] = useState(replaces?.doc_number || "");
  const [docDate, setDocDate] = useState("");
  const [expiry, setExpiry] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(0);
  const pick = useRef(null);
  const single = files.length === 1;
  const tooBig = files.filter((f) => f.size > (state?.max_mb || 50) * 1048576);

  const submit = async () => {
    if (!files.length) return toast.error("Choose a file first");
    if (tooBig.length) return toast.error(`${tooBig[0].name} is larger than ${state?.max_mb} MB`);
    setBusy(true); let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f); fd.append("folder", folder || "Other");
      if (single && title) fd.append("title", title);
      if (single && docNumber) fd.append("doc_number", docNumber);
      if (docDate) fd.append("doc_date", docDate);
      if (expiry) fd.append("expiry_date", expiry);
      if (notes) fd.append("notes", notes);
      if (replaces) fd.append("replaces_id", replaces.id);
      try { await api.post(`/projects/${projectId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); ok += 1; setDone(ok); }
      catch (err) { toast.error(`${f.name}: ${getErrorMessage(err)}`); }
    }
    setBusy(false);
    if (ok) { toast.success(ok === 1 ? "Document uploaded" : `${ok} documents uploaded`); onDone(); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{replaces ? `New version of “${replaces.title}”` : "Upload documents"}</DialogTitle>
          <DialogDescription>{replaces ? `This becomes v${replaces.version + 1}; the earlier versions stay in the history.` : "Details apply to every file selected."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-dashed border-border p-3 text-sm">
            {files.length ? (
              <ul className="space-y-1">{files.map((f) => <li key={f.name} className="flex justify-between gap-2"><span className="truncate">{f.name}</span><span className={`shrink-0 text-xs ${f.size > (state?.max_mb || 50) * 1048576 ? "text-rose-500" : "text-muted-foreground"}`}>{sizeText(f.size)}</span></li>)}</ul>
            ) : <p className="text-muted-foreground">No file chosen.</p>}
            <input ref={pick} type="file" multiple={!replaces} hidden onChange={(e) => e.target.files?.length && setFiles(Array.from(e.target.files))} />
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => pick.current?.click()}><UploadCloud className="h-4 w-4" /> {files.length ? "Change" : "Choose file"}</Button>
          </div>
          {single && <div><Label>Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={files[0].name.replace(/\.[^.]+$/, "")} data-testid="document-title-input" /></div>}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Folder</Label><FolderField value={folder} onChange={setFolder} folders={folders} /></div>
            {single && <div><Label>Document no.</Label><Input value={docNumber} onChange={(e) => setDocNumber(e.target.value)} placeholder="e.g. DWG-STR-014" /></div>}
            <div><Label>Document date</Label><Input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} /></div>
            <div><Label>Expiry date <span className="text-muted-foreground">(optional)</span></Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} /></div>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !files.length} data-testid="document-upload-submit">{busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {done}/{files.length}</> : "Upload"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ projectId, doc, folders, onClose, onDone }) {
  const [f, setF] = useState({ title: doc.title, folder: doc.folder, doc_number: doc.doc_number || "", doc_date: doc.doc_date || "", expiry_date: doc.expiry_date || "", notes: doc.notes || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/projects/${projectId}/documents/${doc.id}`, { ...f, doc_number: f.doc_number || null, doc_date: f.doc_date || null, expiry_date: f.expiry_date || null, notes: f.notes || null });
      toast.success("Details saved"); onDone();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit details</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={f.title} onChange={set("title")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Folder</Label><FolderField value={f.folder} onChange={(v) => setF((s) => ({ ...s, folder: v }))} folders={folders} /></div>
            <div><Label>Document no.</Label><Input value={f.doc_number} onChange={set("doc_number")} /></div>
            <div><Label>Document date</Label><Input type="date" value={f.doc_date} onChange={set("doc_date")} /></div>
            <div><Label>Expiry date</Label><Input type="date" value={f.expiry_date} onChange={set("expiry_date")} /></div>
          </div>
          <div><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={busy}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
